import React, { memo, useMemo } from 'react'
import { Cell, Column, Row, SortingRule } from 'react-table'
import { ServiceState } from '@racepointenergy/swc/scs/features/host-states'

import { Marking, Mark } from 'components/Mark'
import { Table } from 'components/Table'
import type { TableHeader } from 'components/Table/types'
import { HomeServicesBlock } from './HomeServicesBlock'
import {
  HomeServicesComponentCell,
  HomeServicesRowStyles,
  HomeServicesTableInnerWrapper,
  HomeServicesTableOuterWrapper,
  HomeServicesTypeCell,
  HomeServicesActiveCellStyles,
} from './styles'

interface HomeServicesDesktopTableProps {
  headers: Record<string, TableHeader>
  services: ServiceState[]
  defaultSortBy: SortingRule<ServiceState>
  sortBy: SortingRule<ServiceState>
  setSortBy: (sortBy: SortingRule<ServiceState>) => void
  searchValue?: string
  onSelectService: (row: Row<ServiceState>) => void
}

const ActiveCell = (cell: Cell) => {
  if (cell.value === undefined) {
    return <></>
  } else if (cell.value) {
    return <Mark marking={Marking.Check} />
  } else {
    return <Mark marking={Marking.None} />
  }
}

const ComponentCell = (cell: Cell) => (
  <HomeServicesComponentCell>{cell.value}</HomeServicesComponentCell>
)

const LabelCell = (cell: Cell) => (
  <HomeServicesTypeCell title={cell.value}>{cell.value}</HomeServicesTypeCell>
)

function HomeServicesTable({
  headers,
  services,
  defaultSortBy,
  sortBy,
  setSortBy,
  searchValue,
  onSelectService,
}: HomeServicesDesktopTableProps) {
  const columns: readonly Column<ServiceState>[] = useMemo(
    () => [
      {
        Header: '',
        id: headers.active.id,
        accessor: (row) => row.isActive,
        Cell: ActiveCell,
        percentWidth: '8%',
        cellStyles: HomeServicesActiveCellStyles,
        sortType: 'basic',
      },
      {
        Header: headers.component.label,
        id: headers.component.id,
        accessor: (row) => row.component,
        Cell: ComponentCell,
        percentWidth: '20%',
      },
      {
        Header: headers.logicalComponent.label,
        id: headers.logicalComponent.id,
        accessor: (row) => row.logicalComponent,
        Cell: LabelCell,
        percentWidth: '20%',
      },
      {
        Header: headers.variant.label,
        id: headers.variant.id,
        accessor: (row) => row.variant,
        percentWidth: '10%',
      },
      {
        Header: headers.serviceType.label,
        id: headers.serviceType.id,
        accessor: (row) => row.serviceType,
        Cell: LabelCell,
        percentWidth: '20%',
      },
      {
        Header: headers.zone.label,
        id: headers.zone.id,
        accessor: (row) => row.zone,
        percentWidth: '20%',
      },
    ],
    [headers]
  )

  return (
    <HomeServicesTableOuterWrapper>
      <HomeServicesTableInnerWrapper>
        <Table
          columns={columns}
          data={services}
          defaultSortBy={defaultSortBy}
          sortBy={sortBy}
          setSortBy={setSortBy}
          searchValue={searchValue}
          rowStyles={HomeServicesRowStyles}
          mobileViewComponent={HomeServicesBlock}
          onRowSelect={onSelectService}
        />
      </HomeServicesTableInnerWrapper>
    </HomeServicesTableOuterWrapper>
  )
}

export default memo(HomeServicesTable)
